'use client'

import React, { useCallback, useState, useEffect } from 'react'
import { useForm, FieldError, FieldErrorsImpl, Merge } from 'react-hook-form'
import { valibotResolver } from '@hookform/resolvers/valibot'
import {
  object,
  string,
  minLength,
  email,
  regex,
  optional,
  literal
} from 'valibot'
import { ValidationError } from '@/app/components/forms/ui/page/validation-error'
import { FieldBlock } from '@/app/components/forms/ui/page/field-block'
import { Select } from '@/app/components/forms/ui/page/select'
import { Textarea } from '@/app/components/forms/ui/page/textarea'
import { useThanksForAppRouter } from '@/hooks/useThanksForAppRouter'
import { Button } from '@/app/components/parts/Button'
import { useSendApi } from '@/app/components/forms/Hook'
import { Checkbox } from '@/app/components/parts/CheckBox'
import { Input } from './ui/page/input'
import { Opportunity } from '@/types/newt/opportunity'

interface PartnershipFormProps {
  opportunities: Opportunity[]
}

const lawguePartnerSchema = object({
  company: string([minLength(1, '必須項目です')]),
  lastname: string([minLength(1, '必須項目です')]),
  firstname: string([minLength(1, '必須項目です')]),
  email: string([
    minLength(1, '必須項目です'),
    email('入力されたメールアドレスに間違いがあります')
  ]),
  phone_number: string([
    minLength(1, '必須項目です'),
    regex(/^\d{2,4}-\d{1,4}-\d{4}$/, '電話番号の形式を確認してください')
  ]),
  mobile_phone: optional(string()),
  department: optional(string()),
  website: optional(string()),
  select_form3: optional(string()),
  select_form4: string([minLength(1, '必須項目です')]),
  textarea: optional(string()),
  checkbox: literal(true, 'チェックを入れてください'),
  first_view_route: optional(string()),
  last_view_route: optional(string())
})

const PartnershipForm: React.FC<PartnershipFormProps> = ({ opportunities }) => {
  const { sendApi, isSendError } = useSendApi(
    '/lawgue-partner',
    '/thanks_contact/'
  )

  const [isButtonClicked, setIsButtonClicked] = useState(false)
  const [firstViewRoute, setFirstViewRoute] = useState('')
  const [lastViewRoute, setLastViewRoute] = useState('')
  const { pushThanks } = useThanksForAppRouter()
  const [localError, setLocalError] = useState(false)

  useEffect(() => {
    const fullPath = `${window.location.pathname}${window.location.search}`

    if (!sessionStorage.getItem('firstViewURL')) {
      sessionStorage.setItem('firstViewURL', fullPath)
    }

    setFirstViewRoute(sessionStorage.getItem('firstViewURL') || '/')
    setLastViewRoute(fullPath)
  }, [])

  const handleOnSubmit = useCallback(
    async (data) => {
      const params = new URLSearchParams(window.location.search)
      const formData = {
        ...data,
        first_view_route: firstViewRoute,
        last_view_route: lastViewRoute,
        utm_params: Object.fromEntries(params)
      }
      setIsButtonClicked(true)
      setLocalError(false)
      try {
        await sendApi(JSON.stringify(formData))
        pushThanks('thanks_contact')
      } catch (error) {
        console.error('Sending API request failed:', error)
        setLocalError(true)
      } finally {
        setIsButtonClicked(false)
      }
    },
    [sendApi, firstViewRoute, lastViewRoute, pushThanks]
  )

  const {
    register,
    handleSubmit,
    formState: { errors: formatError, isLoading }
  } = useForm({
    resolver: valibotResolver(lawguePartnerSchema)
  })

  return (
    <form className="mt-6 lg:mt-14" onSubmit={handleSubmit(handleOnSubmit)}>
      <fieldset>
        <div className="w-full mx-auto">
          <input type="hidden" name="first_view_route" defaultValue="/" />
          <input type="hidden" name="last_view_route" defaultValue="/contact" />
          <div className="relative mb-6 done_input">
            <FieldBlock
              labelText={'貴社名／貴所名'}
              htmlFor={'company'}
              required
              error={formatError.company as FieldError}
            >
              <Input
                id={'company'}
                type="text"
                placeholder="FRAIM株式会社"
                autoComplete="company"
                register={register('company')}
              />
            </FieldBlock>
          </div>
          <div className="relative mb-6 done_input">
            <div className="flex flex-col lg:flex-row">
              <div className="w-full lg:w-1/2 mb-6 lg:mb-0 lg:mr-6 relative done_input">
                <FieldBlock
                  labelText={'姓'}
                  htmlFor={'lastname'}
                  required
                  error={formatError.lastname as FieldError}
                >
                  <Input
                    id={'lastname'}
                    type="text"
                    placeholder="山田"
                    autoComplete="family-name"
                    register={register('lastname')}
                  />
                </FieldBlock>
              </div>
              <div className="w-full lg:w-1/2 relative done_input">
                <FieldBlock
                  labelText={'名'}
                  htmlFor={'firstname'}
                  required
                  error={formatError.firstname as FieldError}
                >
                  <Input
                    id={'firstname'}
                    type="text"
                    placeholder="太郎"
                    autoComplete="given-name"
                    register={register('firstname')}
                  />
                </FieldBlock>
              </div>
            </div>
          </div>
          <div className="relative my-6 done_input">
            <FieldBlock
              labelText={'メールアドレス'}
              htmlFor={'email'}
              required
              error={formatError.email as FieldError}
            >
              <Input
                id={'email'}
                type="email"
                placeholder="lawgue@mail.com"
                autoComplete="email"
                register={register('email')}
              />
            </FieldBlock>
          </div>
          <div className="relative my-6 done_input">
            <FieldBlock
              labelText={'電話番号（ハイフンあり）'}
              htmlFor={'phone_number'}
              required
              error={formatError.phone_number as FieldError}
            >
              <Input
                id={'phone_number'}
                type="text"
                placeholder="03-0000-0000"
                autoComplete="phone_number"
                register={register('phone_number')}
              />
            </FieldBlock>
          </div>
          <div className="relative my-6 done_input">
            <FieldBlock
              labelText={'携帯電話番号（ハイフンあり）'}
              htmlFor={'mobile_phone'}
            >
              <Input
                id={'mobile_phone'}
                type="text"
                placeholder="080-0000-0000"
                autoComplete="mobile_phone"
                register={register('mobile_phone')}
              />
            </FieldBlock>
          </div>
          <div className="relative my-6 done_input">
            <FieldBlock labelText={'部署名'} htmlFor={'department'}>
              <Input
                id={'department'}
                type="text"
                placeholder="法務部"
                autoComplete="department"
                register={register('department')}
              />
            </FieldBlock>
          </div>
          <div className="relative my-6 done_input">
            <FieldBlock labelText={'会社URL'} htmlFor={'website'}>
              <Input
                id={'website'}
                type="text"
                placeholder="https://fraim.co.jp"
                autoComplete="website"
                register={register('website')}
              />
            </FieldBlock>
          </div>
          <div className="relative mt-6 done_input">
            <FieldBlock
              labelText={'LAWGUEを知ったきっかけ'}
              htmlFor={'select_form3'}
            >
              <Select
                id={'select_form3'}
                register={register('select_form3')}
                options={opportunities.map((opt) => ({
                  label: opt.label,
                  value: opt.value
                }))}
              />
            </FieldBlock>
          </div>
        </div>
        <div className="relative mt-6 done_input">
          <FieldBlock
            labelText={'ご相談内容'}
            htmlFor={'select_form4'}
            required
            error={formatError.select_form4 as FieldError}
          >
            <Select
              id={'select_form4'}
              register={register('select_form4')}
              options={[
                {
                  label: '選択してください。',
                  value: ''
                },
                {
                  label: 'セールスパートナーについて',
                  value: 'セールスパートナーについて'
                },
                {
                  label: '紹介パートナーについて',
                  value: '紹介パートナーについて'
                },
                {
                  label: 'まずは話を聞いてみたい',
                  value: 'まずは話を聞いてみたい'
                }
              ]}
            />
          </FieldBlock>
        </div>
        <div className="relative mt-6">
          <FieldBlock labelText={'お問い合わせ内容詳細'} htmlFor={'textarea'}>
            <Textarea
              id={'textarea'}
              placeholder="*お問い合わせについてご記入ください"
              autoComplete="textarea"
              register={register('textarea')}
            />
          </FieldBlock>
        </div>
        <div className="relative my-6">
          <Checkbox
            htmlfor="checkbox"
            required
            require={true}
            register={register}
          />
          {formatError.checkbox && (
            <ValidationError error={formatError.checkbox as FieldError} />
          )}
        </div>
      </fieldset>
      <div className="text-center mt-6 lg:mt-14">
        <Button
          size="medium"
          bg="visible"
          isLoading={isButtonClicked || isLoading}
          label={isButtonClicked ? '送信中' : '送信する'}
          type="submit"
        />
      </div>
      {(isSendError || localError) && (
        <div className="py-4 px-6 mt-10 bg-[#FF413614] border-[#FF4136] border-solid border-[1px] rounded-[4px] text-left">
          <p className="text-base text-[#FF4136]">
            エラーが起こったため送信することができませんでした
          </p>
          <p className="text-base mt-6">
            お手数ですがもう一度ご送信ください。
            <br />
            それでも送信できない場合は、すこし時間を置いてお試しくださいませ。
          </p>
        </div>
      )}
    </form>
  )
}

export default PartnershipForm
